import type { PointerEvent as ReactPointerEvent } from 'react';
import { PanelRightOpen, PanelRightClose } from 'lucide-react';

interface PanelResizerProps {
  onResizeStart: (e: ReactPointerEvent<HTMLDivElement>) => void;
  onExpand: () => void;
  collapsed: boolean;
}

/**
 * Sits between the canvas and the inspector panel.
 * Expanded: a thin drag handle on the panel's left edge.
 * Collapsed: a slim rail with an expand button (so the panel can be reopened).
 * On small screens the inspector is an overlay sheet instead, so the rail is
 * hidden — open it from the toolbar's style button.
 */
export function PanelResizer({ onResizeStart, onExpand, collapsed }: PanelResizerProps) {
  if (collapsed) {
    return (
      <button
        type="button"
        onClick={onExpand}
        title="Expand inspector"
        className="group hidden w-8 shrink-0 items-center justify-center border-l border-line bg-bg-soft text-muted transition-colors hover:bg-accent/10 hover:text-accent lg:flex"
      >
        <PanelRightOpen size={14} className="transition-transform group-hover:-translate-x-0.5" />
      </button>
    );
  }
  return (
    <div
      onPointerDown={onResizeStart}
      title="Drag to resize the inspector"
      className="group relative hidden w-[7px] shrink-0 cursor-col-resize bg-card-2/60 transition-colors hover:bg-accent/20 lg:block"
    >
      <div className="absolute inset-y-0 left-1/2 w-px -translate-x-1/2 bg-muted/20 transition-colors group-hover:bg-accent/70" />
      <div className="pointer-events-none absolute inset-y-0 left-1/2 flex -translate-x-1/2 items-center opacity-0 transition-opacity group-hover:opacity-100">
        <PanelRightClose size={9} className="text-accent" />
      </div>
    </div>
  );
}
